import React, { useState, useEffect, useRef } from "react";
import styles from "../EditDiv/EditDiv.module.css";
import buttonStyles from "../YesNoButtonsCSS/YesNoButtons.module.css";
import BlueLogo from "../../assets/BlueLogo.png";
import { Grid, TextField, CircularProgress } from "@mui/material";
import { Form, FormikProvider } from "formik";
import { useFormik } from "formik";
import * as Yup from "yup";
import textfieldStyle from "../../GlobalStyles/styles";
import Dropdown from "../DropDownBox/DropdownV2";
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from "../../redux/store";
import { submitStructuralSpecAndDownloadDocx, setSuccess, hasError } from "../../redux/slices/structuralSpecification";
import { useMsal } from "@azure/msal-react";
import StructuralSpecSuccessModal from "./StructuralSpecSuccessModal";

const StructuralSpecificationForm = ({ onSubmit, onClose, defaultEmail, initialJobNumber, initialAddress }) => {
  const initialValues = {
    jobNumber: initialJobNumber || "",
    address: initialAddress || "",
    sections: [],
    groundType: "",
    customGroundType: "",
    shallowFoundationDepth: "",
    bearingPressureGround: "",
    bearingPressureBasement: "",
    groundInvestigationNumber: "",
    steelworkConnections: "",
    revision: "",
    requesterEmail: "",
  };

  const dispatch = useDispatch();
  const structuralState = useSelector((state) => state.structuralSpecification || {});
  const [apiResponse, setApiResponse] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const { accounts } = useMsal();
  const account = accounts && accounts.length > 0 ? accounts[0] : null;
  const backdropRef = useRef(null);
  const formRef = useRef(null);
  const [showOtherGroundType, setShowOtherGroundType] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Section options for multi-select dropdown
  const sectionOptions = [
    { label: "Demolition", value: "Demolition" },
    { label: "Excavation and Filling", value: "Excavation and Filling" },
    { label: "Foundations", value: "Foundations" },
    { label: "Underpinning", value: "Underpinning" },
    { label: "Insitu Concrete", value: "Insitu Concrete" },
    { label: "Masonry", value: "Masonry" },
    { label: "Structural Timber", value: "Structural Timber" },
    { label: "Structural Steelwork", value: "Structural Steelwork" },
    { label: "Lintels", value: "Lintels" },
    { label: "Drainage", value: "Drainage" },
    { label: "Preloading Beams", value: "Preloading Beams" },
    { label: "Pile Specification", value: "Pile Specification" }
  ];

  // Ground type options
  const groundTypeOptions = [
    { label: "London Clay", value: "London Clay" },
    { label: "Sand & Gravel", value: "Sand & Gravel" },
    { label: "Other", value: "Other" }
  ];

  // Steelwork connections options
  const steelworkOptions = [
    "Yes",
    "No, it's the Contractor's responsibility"
  ];

  // clear any previous success state when this form mounts so it won't auto-close
  React.useEffect(() => {
    dispatch(setSuccess(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reflect redux state into local UI
  React.useEffect(() => {
    if (structuralState.successMessage) {
      const msg = structuralState.successMessage;
      enqueueSnackbar(msg, { variant: 'success' });
      // clear redux success so subsequent opens don't immediately close
      dispatch(setSuccess(null));
      // close form on success
      if (typeof onClose === 'function') onClose();
    }
    if (structuralState.error) {
      const errMsg = structuralState.error?.message || String(structuralState.error);
      enqueueSnackbar(errMsg, { variant: 'error' });
      // keep error in redux for debugging but clear it so UI won't be sticky
      dispatch(hasError(null));
    }
  }, [structuralState.successMessage, structuralState.error, enqueueSnackbar, onClose]);

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object().shape({
      jobNumber: Yup.number().min(0, "Must be a positive number").required("Required"),
      address: Yup.string().required("Required"),
      sections: Yup.array().min(1, "Please select at least one section").required("Required"),
      groundType: Yup.string(),
      customGroundType: Yup.string().test('custom-ground-type-required', 'Please specify the ground type', function (value) {
        const { groundType } = this.parent;
        if (groundType === 'Other') {
          return value && value.trim().length > 0;
        }
        return true;
      }),
      shallowFoundationDepth: Yup.string(),
      bearingPressureGround: Yup.string(),
      bearingPressureBasement: Yup.string(),
      groundInvestigationNumber: Yup.string(),
      steelworkConnections: Yup.string().required("Required"),
      revision: Yup.string(),
    }),
    onSubmit: async (values) => {
      const finalGroundType = values.groundType === "Other" ? values.customGroundType : values.groundType;
      const payload = {
        jobNumber: values.jobNumber || "",
        address: values.address || "",
        sections: values.sections || [],
        groundType: finalGroundType || "",
        shallowFoundationDepth: values.shallowFoundationDepth || "",
        bearingPressureGround: values.bearingPressureGround || "",
        bearingPressureBasement: values.bearingPressureBasement || "",
        groundInvestigationNumber: values.groundInvestigationNumber || "",
        steelworkConnections: values.steelworkConnections || "",
        revision: values.revision || "",
        requesterEmail: values.requesterEmail || "",
      };

      try {
        await dispatch(submitStructuralSpecAndDownloadDocx(payload));
        setShowSuccessModal(true);
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || 'Failed to generate document';
        enqueueSnackbar(msg, { variant: 'error' });
      }
    },
  });

  // Set requesterEmail from MSAL username or provided defaultEmail
  useEffect(() => {
    const autoEmail = (typeof defaultEmail === 'string' && defaultEmail) || account?.username || '';
    if (autoEmail) formik.setFieldValue('requesterEmail', autoEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultEmail, account]);

  // Check if "Other" is selected for ground type
  useEffect(() => {
    setShowOtherGroundType(formik.values.groundType === "Other");
  }, [formik.values.groundType]);

  // Ensure modal opens scrolled to the top and content is top-aligned
  useEffect(() => {
    if (backdropRef.current) backdropRef.current.scrollTop = 0;
    if (formRef.current) formRef.current.scrollTop = 0;
  }, []);

  const handleFileChange = (event) => {
    const files = event.target.files;
    formik.setFieldValue('attachments', files);
  };

  if (showSuccessModal) {
    return (
      <StructuralSpecSuccessModal
        isVisible={true}
        onClose={() => {
          setShowSuccessModal(false);
          if (typeof onClose === 'function') onClose();
        }}
      />
    );
  }

  return (
    <div className={styles.backGround} ref={backdropRef}>
      <div
        className={styles.form}
        ref={formRef}
        style={{
          width: "600px",
          maxHeight: "80vh",
          overflowY: "auto",
          justifyContent: "flex-start",
          alignItems: "stretch",
          paddingTop: 12,
          paddingBottom: 8,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.momentTitle}>MOMENT</div>
        <div className={styles.sectionTitle}>Structural Specification</div>

        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate style={{ width: "100%" }} onSubmit={formik.handleSubmit}>
            {/* Job Number */}
            <Grid container sx={{ width: "100%" }}>
              <Grid item xs={4} sx={{ pr: 3 }}>
                <p className={styles.dataTitleV2}>1. Job Number? *</p>
              </Grid>
              <Grid item xs={8} sx={{ mt: 1 }}>
                <TextField
                  fullWidth
                  type="number"
                  value={formik.values.jobNumber}
                  onChange={(e) => formik.setFieldValue("jobNumber", e.target.value)}
                  placeholder="Enter job number"
                  sx={textfieldStyle}
                  error={Boolean(formik.touched.jobNumber && formik.errors.jobNumber)}
                  helperText={formik.touched.jobNumber && formik.errors.jobNumber}
                />
              </Grid>
            </Grid>

            {/* Address */}
            <Grid container sx={{ width: "100%", mt: 2 }}>
              <Grid item xs={4} sx={{ pr: 3 }}>
                <p className={styles.dataTitleV2}>2. Address? *</p>
              </Grid>
              <Grid item xs={8} sx={{ mt: 1 }}>
                <TextField
                  fullWidth
                  value={formik.values.address}
                  onChange={(e) => formik.setFieldValue("address", e.target.value)}
                  placeholder="Enter address"
                  sx={textfieldStyle}
                  error={Boolean(formik.touched.address && formik.errors.address)}
                  helperText={formik.touched.address && formik.errors.address}
                />
              </Grid>
            </Grid>

            {/* Sections */}
            <Grid container sx={{ width: "100%", mt: 2 }}>
              <Grid item xs={4} sx={{ pr: 3 }}>
                <p className={styles.dataTitleV2}>3. Which Sections would you like to include in the Structural Specification Document? *</p>
              </Grid>
              <Grid item xs={8} sx={{ mt: 1 }}>
                <Dropdown
                  options={sectionOptions}
                  mode="multi"
                  placeholder={"Select sections..."}
                  initialSelectedValue={formik.values.sections || []}
                  onValueChange={(selectedSections) => formik.setFieldValue("sections", selectedSections)}
                />
                {formik.touched.sections && formik.errors.sections && (
                  <div style={{ color: "#f44336", fontSize: "12px", marginTop: "4px" }}>
                    {formik.errors.sections}
                  </div>
                )}
              </Grid>
            </Grid>

            {/* Ground Type */}
            <Grid container sx={{ width: "100%", mt: 2 }}>
              <Grid item xs={4} sx={{ pr: 3 }}>
                <p className={styles.dataTitleV2}>4. The Ground is Formed from? (Leave blank if N/A)</p>
              </Grid>
              <Grid item xs={8} sx={{ mt: 1 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <Dropdown
                    options={groundTypeOptions}
                    placeholder={"Select..."}
                    initialSelectedValue={formik.values.groundType && formik.values.groundType !== "Other" ? { label: formik.values.groundType, value: formik.values.groundType } : { label: "", value: "" }}
                    onValueChange={(val) => {
                      const selectedValue = val?.value || "";
                      formik.setFieldValue("groundType", selectedValue);
                      setShowOtherGroundType(selectedValue === "Other");
                      if (selectedValue !== "Other") {
                        formik.setFieldValue("customGroundType", "");
                      }
                    }}
                  />
                  {showOtherGroundType && (
                    <TextField
                      fullWidth
                      value={formik.values.customGroundType || ""}
                      onChange={(e) => formik.setFieldValue("customGroundType", e.target.value)}
                      placeholder="Please specify the ground type"
                      sx={textfieldStyle}
                    />
                  )}
                </div>
              </Grid>
            </Grid>

            {/* Shallow Foundation Depth */}
            <Grid container sx={{ width: "100%", mt: 2 }}>
              <Grid item xs={4} sx={{ pr: 3 }}>
                <p className={styles.dataTitleV2}>5. Depth of Shallow Foundations? (Leave blank if N/A)</p>
                <div style={{ fontSize: 12, color: "#666", marginTop: "4px" }}>
                  Do Not type units - Assumed mm BGL
                </div>
              </Grid>
              <Grid item xs={8} sx={{ mt: 1 }}>
                <TextField
                  fullWidth
                  value={formik.values.shallowFoundationDepth}
                  onChange={(e) => formik.setFieldValue("shallowFoundationDepth", e.target.value)}
                  placeholder="Enter your answer"
                  sx={textfieldStyle}
                />
              </Grid>
            </Grid>

            {/* Bearing Pressure Ground */}
            <Grid container sx={{ width: "100%", mt: 2 }}>
              <Grid item xs={4} sx={{ pr: 3 }}>
                <p className={styles.dataTitleV2}>6. Bearing Pressure @ Ground Level? (Leave blank if N/A)</p>
                <div style={{ fontSize: 12, color: "#666", marginTop: "4px" }}>
                  Do Not type units - Assumed kN/m2
                </div>
              </Grid>
              <Grid item xs={8} sx={{ mt: 1 }}>
                <TextField
                  fullWidth
                  value={formik.values.bearingPressureGround}
                  onChange={(e) => formik.setFieldValue("bearingPressureGround", e.target.value)}
                  placeholder="Enter your answer"
                  sx={textfieldStyle}
                />
              </Grid>
            </Grid>

            {/* Bearing Pressure Basement */}
            <Grid container sx={{ width: "100%", mt: 2 }}>
              <Grid item xs={4} sx={{ pr: 3 }}>
                <p className={styles.dataTitleV2}>7. Bearing Pressure @ Basement Level? (Leave blank if N/A)</p>
                <div style={{ fontSize: 12, color: "#666", marginTop: "4px" }}>
                  Do Not type units - Assumed kN/m2
                </div>
              </Grid>
              <Grid item xs={8} sx={{ mt: 1 }}>
                <TextField
                  fullWidth
                  value={formik.values.bearingPressureBasement}
                  onChange={(e) => formik.setFieldValue("bearingPressureBasement", e.target.value)}
                  placeholder="Enter your answer"
                  sx={textfieldStyle}
                />
              </Grid>
            </Grid>

            {/* Ground Investigation Number */}
            <Grid container sx={{ width: "100%", mt: 2 }}>
              <Grid item xs={4} sx={{ pr: 3 }}>
                <p className={styles.dataTitleV2}>8. Ground Investigation Report Number (Leave blank if N/A)</p>
              </Grid>
              <Grid item xs={8} sx={{ mt: 1 }}>
                <TextField
                  fullWidth
                  value={formik.values.groundInvestigationNumber}
                  onChange={(e) => formik.setFieldValue("groundInvestigationNumber", e.target.value)}
                  placeholder="Enter your answer"
                  sx={textfieldStyle}
                />
              </Grid>
            </Grid>

            {/* Steelwork Connections */}
            <Grid container sx={{ width: "100%", mt: 2 }}>
              <Grid item xs={4} sx={{ pr: 3 }}>
                <p className={styles.dataTitleV2}>9. Are we designing the Steelwork Connections on this Job? *</p>
              </Grid>
              <Grid item xs={8} sx={{ mt: 1 }}>
                <div style={{ display: "flex", gap: "16px" }}>
                  {steelworkOptions.map((option) => (
                    <div
                      key={option}
                      onClick={() => formik.setFieldValue("steelworkConnections", option)}
                      style={{
                        padding: "8px 12px",
                        borderRadius: 4,
                        border: formik.values.steelworkConnections === option ? "2px solid #3C96D4" : "1px solid #ddd",
                        cursor: "pointer",
                        background: formik.values.steelworkConnections === option ? "#e9f5fb" : "#fff",
                        fontSize: "14px",
                      }}
                    >
                      {option}
                    </div>
                  ))}
                </div>
                {formik.touched.steelworkConnections && formik.errors.steelworkConnections && (
                  <div style={{ color: "#f44336", fontSize: "12px", marginTop: "4px" }}>
                    {formik.errors.steelworkConnections}
                  </div>
                )}
              </Grid>
            </Grid>

            {/* Revision */}
            <Grid container sx={{ width: "100%", mt: 2 }}>
              <Grid item xs={4} sx={{ pr: 3 }}>
                <p className={styles.dataTitleV2}>10. Revision? (Leave blank if Revision A)</p>
              </Grid>
              <Grid item xs={8} sx={{ mt: 1 }}>
                <TextField
                  fullWidth
                  value={formik.values.revision}
                  onChange={(e) => formik.setFieldValue("revision", e.target.value)}
                  placeholder="Enter revision"
                  sx={textfieldStyle}
                />
              </Grid>
            </Grid>

            <div className={styles.updateAndCancel} style={{ marginTop: 16 }}>
              <div className={styles.updateButton} type="submit" onClick={formik.handleSubmit}>
                <div className={buttonStyles.YesButtonComponentStyling}>
                  {structuralState.isSubmitting ? <CircularProgress size={18} style={{ color: '#fff' }} /> : 'SUBMIT'}
                </div>
              </div>
              <div className={styles.cancelButton} onClick={onClose}>
                <div className={buttonStyles.NoButtonComponentStyling}>CANCEL</div>
              </div>
            </div>
          </Form>
        </FormikProvider>

        <img className={styles.blueLogo} src={BlueLogo} alt="BlueLogo" style={{ alignSelf: 'center' }} />
      </div>


    </div>
  );
};

export default StructuralSpecificationForm;
